import { useState } from 'react';
import { toast } from 'react-hot-toast';
import { Heart, Smile, Meh, Frown, ThermometerSun, Activity, MessageSquare } from 'lucide-react';
import { apiClient } from '@/services/api/client';

type MoodLevel = 1 | 2 | 3 | 4 | 5;

interface DailyLogData {
  mood: MoodLevel | null;
  symptoms: string[];
  notes: string;
  temperature?: number;
  bloodPressure?: string;
}

const MOOD_OPTIONS = [
  { level: 5, icon: Smile, label: 'Ótimo', color: 'text-green-600 bg-green-100' },
  { level: 4, icon: Smile, label: 'Bem', color: 'text-blue-600 bg-blue-100' },
  { level: 3, icon: Meh, label: 'Normal', color: 'text-yellow-600 bg-yellow-100' },
  { level: 2, icon: Frown, label: 'Mal', color: 'text-orange-600 bg-orange-100' },
  { level: 1, icon: Frown, label: 'Muito Mal', color: 'text-red-600 bg-red-100' },
];

const COMMON_SYMPTOMS = [
  'Dor de cabeça',
  'Febre',
  'Náusea',
  'Tontura',
  'Fadiga',
  'Dor no corpo',
  'Tosse',
  'Dificuldade para dormir',
];

export function DailyHealthLog() {
  const [isOpen, setIsOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [logData, setLogData] = useState<DailyLogData>({
    mood: null,
    symptoms: [],
    notes: '',
  });

  const handleSubmit = async () => {
    if (!logData.mood) {
      toast.error('Selecione como você está se sentindo');
      return;
    }

    setSaving(true);
    try {
      await apiClient.post('patient/health-logs', {
        date: new Date().toISOString(),
        mood: logData.mood,
        symptoms: logData.symptoms,
        notes: logData.notes,
        temperature: logData.temperature,
        bloodPressure: logData.bloodPressure,
      });

      toast.success('Registro salvo com sucesso!');
      setIsOpen(false);
      setLogData({ mood: null, symptoms: [], notes: '' });
    } catch (error) {
      toast.error('Erro ao salvar registro');
    } finally {
      setSaving(false);
    }
  };

  const toggleSymptom = (symptom: string) => {
    setLogData((prev) => ({
      ...prev,
      symptoms: prev.symptoms.includes(symptom)
        ? prev.symptoms.filter((s) => s !== symptom)
        : [...prev.symptoms, symptom],
    }));
  };

  if (!isOpen) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Heart className="h-5 w-5 text-red-500" />
            Como você está hoje?
          </h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          Registre diariamente como você está se sentindo e seus sintomas
        </p>
        <button
          onClick={() => setIsOpen(true)}
          className="w-full py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 font-medium"
        >
          Registrar Hoje
        </button>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
          <Heart className="h-5 w-5 text-red-500" />
          Registro Diário
        </h3>
        <button
          onClick={() => setIsOpen(false)}
          className="text-gray-400 hover:text-gray-600"
        >
          ✕
        </button>
      </div>

      <div className="space-y-6">
        {/* Mood Selection */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Como você está se sentindo?
          </label>
          <div className="grid grid-cols-5 gap-2">
            {MOOD_OPTIONS.map((option) => {
              const Icon = option.icon;
              const isSelected = logData.mood === option.level;
              return (
                <button
                  key={option.level}
                  onClick={() => setLogData({ ...logData, mood: option.level as MoodLevel })}
                  className={`flex flex-col items-center gap-2 p-3 rounded-lg border-2 transition-all ${
                    isSelected
                      ? `${option.color} border-current`
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Icon className={`h-6 w-6 ${isSelected ? '' : 'text-gray-400'}`} />
                  <span className={`text-xs font-medium ${isSelected ? '' : 'text-gray-600'}`}>
                    {option.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Symptoms */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-3">
            Algum sintoma? (opcional)
          </label>
          <div className="grid grid-cols-2 gap-2">
            {COMMON_SYMPTOMS.map((symptom) => {
              const isSelected = logData.symptoms.includes(symptom);
              return (
                <button
                  key={symptom}
                  onClick={() => toggleSymptom(symptom)}
                  className={`px-3 py-2 text-sm rounded-lg border transition-colors ${
                    isSelected
                      ? 'bg-purple-100 border-purple-300 text-purple-800 font-medium'
                      : 'border-gray-200 text-gray-700 hover:border-gray-300'
                  }`}
                >
                  {symptom}
                </button>
              );
            })}
          </div>
        </div>

        {/* Vitals - Optional */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <ThermometerSun className="h-4 w-4 inline mr-1" />
              Temperatura (°C)
            </label>
            <input
              type="number"
              step="0.1"
              placeholder="36.5"
              value={logData.temperature || ''}
              onChange={(e) => setLogData({ ...logData, temperature: parseFloat(e.target.value) })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              <Activity className="h-4 w-4 inline mr-1" />
              Pressão (mmHg)
            </label>
            <input
              type="text"
              placeholder="120/80"
              value={logData.bloodPressure || ''}
              onChange={(e) => setLogData({ ...logData, bloodPressure: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Notes */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            <MessageSquare className="h-4 w-4 inline mr-1" />
            Observações (opcional)
          </label>
          <textarea
            rows={3}
            placeholder="Como você está se sentindo? Algo importante para registrar?"
            value={logData.notes}
            onChange={(e) => setLogData({ ...logData, notes: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent resize-none"
          />
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => setIsOpen(false)}
            className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving || !logData.mood}
            className="flex-1 px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Salvando...' : 'Salvar Registro'}
          </button>
        </div>
      </div>
    </div>
  );
}
